import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import ReviewCard from "@/components/base/ReviewCard";
import { searchMovies, searchReviews } from "@/utils/catalog";
import { movies } from "@/mocks/movies";
import { reviews } from "@/mocks/reviews";

const hotKeywords = ["东京暮色", "白色回声", "纪录", "悬疑", "影评", "东京"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";

  usePageMeta(
    keyword ? `搜索「${keyword}」 - 苍井空电影大全` : "影视内容搜索 - 苍井空电影大全",
    keyword
      ? `在苍井空电影站内搜索「${keyword}」，可分别查看匹配的影视作品与影评文章，支持在两类结果间切换。`
      : "输入电影名称、导演、题材或影评关键词，在苍井空电影站内检索影视作品与影评文章。",
  );
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"movie" | "review">("movie");

  useEffect(() => {
    setInput(keyword);
    setTab("movie");
  }, [keyword]);

  const movieResults = useMemo(() => searchMovies(keyword), [keyword]);
  const reviewResults = useMemo(() => searchReviews(keyword), [keyword]);
  const total = movieResults.length + reviewResults.length;

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q });
  };

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1320px] px-4 py-6 md:px-8 md:py-8">
          <Breadcrumb items={[{ label: "搜索结果" }]} />
          <h1 className="mt-4 font-heading text-2xl text-foreground-950 md:text-[32px]">搜索影视内容</h1>
          <p className="mt-1.5 text-xs text-foreground-500">
            在电影标题、简介、演职员与影评正文中进行关键词匹配。
          </p>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(input);
            }}
            className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center"
          >
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入电影名称、导演、题材或影评关键词"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {input ? (
                <button
                  type="button"
                  onClick={() => {
                    setInput("");
                    setSearchParams({});
                  }}
                  aria-label="清空关键词"
                  className="flex h-5 w-5 items-center justify-center text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-circle-fill text-sm" />
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-search-line text-base" />
              搜索
            </button>
          </form>

          {keyword ? (
            <p className="mt-4 text-xs text-foreground-600">
              关键词「<span className="text-primary-700">{keyword}</span>」共找到{" "}
              <span className="font-medium text-foreground-900">{total}</span> 条结果
            </p>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门搜索：</span>
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  onClick={() => submit(word)}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  {word}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1320px] px-4 py-8 md:px-8">
        {keyword ? (
          <>
            <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1 sm:inline-flex">
              <button
                onClick={() => setTab("movie")}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors ${
                  tab === "movie"
                    ? "bg-background-50 text-primary-700"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                <i className="ri-film-line text-sm" />
                电影（{movieResults.length}）
              </button>
              <button
                onClick={() => setTab("review")}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors ${
                  tab === "review"
                    ? "bg-background-50 text-primary-700"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                <i className="ri-article-line text-sm" />
                影评（{reviewResults.length}）
              </button>
            </div>

            <div className="mt-6">
              {tab === "movie" ? (
                movieResults.length ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {movieResults.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} variant="row" />
                    ))}
                  </div>
                ) : (
                  <EmptyState keyword={keyword} type="电影" />
                )
              ) : reviewResults.length ? (
                <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                  {reviewResults.map((review) => (
                    <ReviewCard key={review.id} review={review} variant="row" />
                  ))}
                </div>
              ) : (
                <EmptyState keyword={keyword} type="影评" />
              )}
            </div>
          </>
        ) : (
          <div>
            <h2 className="font-heading text-lg text-foreground-950">开始搜索</h2>
            <p className="mt-1 text-xs text-foreground-500">
              输入关键词后，可分别在电影与影评两类结果之间切换查看。
            </p>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
                <h3 className="font-heading text-sm text-foreground-950">最近更新的作品</h3>
                <ul className="mt-3 space-y-2 text-xs text-foreground-600">
                  {movies.slice(0, 5).map((movie) => (
                    <li key={movie.id}>
                      <Link
                        to={`/detail/${movie.id}`}
                        className="flex items-center justify-between gap-3 transition-colors hover:text-primary-700"
                      >
                        <span className="truncate">{movie.title}</span>
                        <span className="shrink-0 text-foreground-400">{movie.year}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
                <h3 className="font-heading text-sm text-foreground-950">最新影评</h3>
                <ul className="mt-3 space-y-2 text-xs text-foreground-600">
                  {reviews.slice(0, 5).map((review) => (
                    <li key={review.id}>
                      <Link
                        to={`/reviews/${review.id}`}
                        className="line-clamp-1 transition-colors hover:text-primary-700"
                      >
                        {review.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}

function EmptyState({ keyword, type }: { keyword: string; type: string }) {
  return (
    <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
        <i className="ri-search-eye-line text-xl leading-none" />
      </span>
      <h2 className="mt-4 font-heading text-base text-foreground-900">未找到相关{type}内容</h2>
      <p className="mt-1.5 text-xs text-foreground-500">
        未找到与「{keyword}」匹配的{type}，请尝试其他关键词或换个分类看看。
      </p>
      <div className="mt-4 flex flex-col items-center justify-center gap-2 sm:flex-row">
        <Link
          to="/category"
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-grid-line text-sm" />
          浏览全部作品
        </Link>
        <Link
          to="/"
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <i className="ri-home-4-line text-sm" />
          返回首页
        </Link>
      </div>
    </div>
  );
}